import type { ComparisonProductInput, RankingWeights } from '../types'
import { DEFAULT_RANKING_WEIGHTS } from '../types'

export interface ScoreBreakdown {
  price: number
  stock: number
  delivery: number
  freight: number
  reliability: number
  total: number
}

/**
 * RankingEngine — weighted score for supplier offers.
 */
export class RankingEngine {
  private weights: RankingWeights

  constructor(weights: RankingWeights = { ...DEFAULT_RANKING_WEIGHTS }) {
    this.weights = { ...weights }
  }

  setWeights(weights: Partial<RankingWeights>): RankingWeights {
    this.weights = this.normalize({ ...this.weights, ...weights })
    return this.weights
  }

  getWeights(): RankingWeights {
    return { ...this.weights }
  }

  /**
   * Scores offers within the same product group (0–100).
   */
  scoreGroup(offers: ComparisonProductInput[]): Array<{ id: string; score: number; breakdown: ScoreBreakdown }> {
    if (offers.length === 0) return []

    const prices = offers.map((o) => o.pixPrice)
    const stocks = offers.map((o) => o.stock)
    const deliveries = offers.map((o) => o.estimatedDelivery)
    const freights = offers.map((o) => o.freight ?? 0)
    const reliabilities = offers.map((o) => effectiveReliability(o))

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const minStock = Math.min(...stocks)
    const maxStock = Math.max(...stocks)
    const minDelivery = Math.min(...deliveries)
    const maxDelivery = Math.max(...deliveries)
    const minFreight = Math.min(...freights)
    const maxFreight = Math.max(...freights)
    const minRel = Math.min(...reliabilities)
    const maxRel = Math.max(...reliabilities)

    return offers.map((offer) => {
      const priceScore = invertNormalize(offer.pixPrice, minPrice, maxPrice)
      const stockScore = normalize(offer.stock, minStock, maxStock)
      const deliveryScore = invertNormalize(offer.estimatedDelivery, minDelivery, maxDelivery)
      const freightScore = invertNormalize(offer.freight ?? 0, minFreight, maxFreight)
      const reliabilityScore = normalize(effectiveReliability(offer), minRel, maxRel)

      const breakdown: ScoreBreakdown = {
        price: round2(priceScore * 100),
        stock: round2(stockScore * 100),
        delivery: round2(deliveryScore * 100),
        freight: round2(freightScore * 100),
        reliability: round2(reliabilityScore * 100),
        total: round2(
          (priceScore * this.weights.price +
            stockScore * this.weights.stock +
            deliveryScore * this.weights.delivery +
            freightScore * this.weights.freight +
            reliabilityScore * this.weights.reliability) *
            100,
        ),
      }

      return { id: offer.id, score: breakdown.total, breakdown }
    })
  }

  private normalize(weights: RankingWeights): RankingWeights {
    const sum =
      weights.price + weights.stock + weights.delivery + weights.freight + weights.reliability
    if (sum <= 0) return { ...DEFAULT_RANKING_WEIGHTS }
    return {
      price: weights.price / sum,
      stock: weights.stock / sum,
      delivery: weights.delivery / sum,
      freight: weights.freight / sum,
      reliability: weights.reliability / sum,
    }
  }
}

/** Reliability + freshness (última atualização) within the 10% reliability weight. */
function effectiveReliability(offer: ComparisonProductInput): number {
  const base = offer.reliability ?? 70
  if (!offer.lastUpdate) return base
  const ageMs = Date.now() - new Date(offer.lastUpdate).getTime()
  if (Number.isNaN(ageMs)) return base
  const ageHours = ageMs / (1000 * 60 * 60)
  const freshnessBoost = ageHours <= 6 ? 10 : ageHours <= 24 ? 5 : ageHours <= 72 ? 2 : 0
  return Math.min(100, base + freshnessBoost)
}

function normalize(value: number, min: number, max: number): number {
  if (max === min) return 1
  return (value - min) / (max - min)
}

function invertNormalize(value: number, min: number, max: number): number {
  return 1 - normalize(value, min, max)
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const rankingEngine = new RankingEngine()
